/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {ServiceError} from '@ion/core/src/types/services';
import {UserNotificationDialog} from '../notifications/UserNotificationDialog';

// defines the AssetDeleteRestrictPrompt Prop type definitions ...
export interface AssetDeleteRestrictPromptProps {
  titleMessage?: string;
  message?: string;
  entity?: string;
  error?: ServiceError | null;
  actionLabel?: string | JSX.Element;
  onDeleteRestrictAction?: () => void;
  onDeleteRestrictCancel?: () => void;
  show: boolean;
};

/**
 * The AssetDeleteRestrictPrompt provides the UI for prompting and canceling an Asset
 * Delete action.
 *
 * ```typescript
 *
 *    import {AssetDeleteRestrictPrompt} from '@ion/dls/src/editors/';
 *
 *    const {
 *      selected,
 *      result,
 *      isEditing,
 *      loading,
 *      error
 *    } = useSelector(({adminAssets: {destroy}}) => destroy);
 *
 *    return (
 *      <AssetDeleteRestrictPrompt show={isEditing}
 *        entity={entity}
 *        titleMessage={titleMessage}
 *        onDeleteRestrictAction={onDeleteAction}
 *        onDeleteRestrictCancel={onDeleteCancel}
 *        message={message} />
 *    );
 *
 * ```
 *
 * @param show When true, the asset delete restriction prompt will be displayed.
 * @param entity The selected Asset that is requested for deleted.
 * @param titleMessage The Asset title by the service.
 * @param message The Asset message by the service before it has been requested.
 * @param error A reference to the ServiceError If the deletion fails.
 * @param onDeleteRestrictAction callback function which will be invoked if the user does some action.
 * @param onDeleteRestrictCancel callback function which will be invoked if the user cancels the operation.
 */
export const AssetDeleteRestrictPrompt = ({
  entity,
  titleMessage,
  message,
  actionLabel,
  onDeleteRestrictAction,
  onDeleteRestrictCancel,
  show = false
}: AssetDeleteRestrictPromptProps) => {
  return (
    <UserNotificationDialog show={show}
      capitalize
      title={translate(titleMessage, {entity})}
      message={message}
      actionLabel={actionLabel}
      dismissLabel={translate('common:editors.shared.close')}
      onAction={onDeleteRestrictAction}
      onDismiss={onDeleteRestrictCancel} />
  );
};

export default AssetDeleteRestrictPrompt;
